import React, { useEffect, useState } from 'react';
import IndexSelect from './IndexSelect';
import VariableInput from './VariableInput';
import SmartBadge from '../../../../components/SmartBadge/SmartBadge';
import RegimeConfluenceHelper from './RegimeConfluenceHelper';
import { getEventObject } from '../../../../utils/EventUtils';
import { getFixedTemplates, findConflict, TEMPLATE_GROUPS } from '../../../../utils/ConditionTemplates';
import { getConditionTemplates, saveConditionTemplate, deleteConditionTemplate } from '../../../../services/AutomationConditionTemplatesService';
import { diagnoseConditions } from '../../../../services/BeholderService';
import { processError } from '../../../../services/ServiceUtils';

// Paleta vívida e distinta (alinhada ao HealthDot): vermelho=ausente, amarelo=parado,
// verde=ok/não atendida, azul=atendida. bg claro + texto escuro + borda/accent saturado.
const CONDITION_PALETTE = {
    red:    { bg: '#fee2e2', fg: '#991b1b', accent: '#ef4444' },
    yellow: { bg: '#fef3c7', fg: '#92400e', accent: '#f59e0b' },
    green:  { bg: '#dcfce7', fg: '#166534', accent: '#22c55e' },
    blue:   { bg: '#dbeafe', fg: '#1e40af', accent: '#3b82f6' }
};

/**
 * props:
 * - indexes
 * - symbol
 * - conditions
 * - onChange
 */
function ConditionsArea(props) {

    const [indexes, setIndexes] = useState([]);
    const [conditions, setConditions] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState({ example: 0 });
    const [addMode, setAddMode] = useState(false);
    const [userTemplates, setUserTemplates] = useState([]);
    const [saveMode, setSaveMode] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [templateError, setTemplateError] = useState('');
    const [condColors, setCondColors] = useState({}); // eval -> 'red'|'yellow'|'green'|'blue'

    function parseConditions(conditionsText) {
        if (!conditionsText) return [];

        const split = conditionsText.split(' && ');
        return split.map(item => {
            const evalStr = item.trim();
            let text = evalStr;

            if (evalStr.startsWith('(!MEMORY[')) {
                // null-guard: (!MEMORY['X'] || MEMORY['X'].field == value)
                text = evalStr
                    .replace(/^\(!MEMORY\['[^']+'\] \|\| /, '')
                    .replace(/\)$/, '');
            }

            text = text
                .replaceAll("MEMORY['", "")
                .replaceAll("']", "")
                .replaceAll("==", "=")
                .replaceAll('.current', '')
                .replaceAll((props.symbol || '') + ":", "")
                .trim();

            if (evalStr.startsWith('(!MEMORY[')) text += ' (ou sem posição)';

            return { eval: evalStr, text };
        });
    }

    function parseIndexes(conditionsArr) {
        const indexesStr = conditionsArr.map(condition => {
            const memoryIndexes = condition.eval.split(/[=><!|()\s]/)
                .filter(str => str.startsWith('MEMORY'))
                .map(str => str.split('.')[0]);
            return memoryIndexes.join(',');
        }).join(',');
        return [...new Set(
            indexesStr
                .replaceAll("MEMORY['", "")
                .replaceAll("']", "")
                .replaceAll('.current', '')
                .split(',')
                .filter(Boolean)
        )].join(',');
    }

    function isConditionValid(evalStr) {
        // null-guard: ausência tratada por design — sempre válida
        if (evalStr.startsWith('(!MEMORY[')) return true;

        const keys = (evalStr.match(/MEMORY\['([^']+)'\]/g) || [])
            .map(m => m.replace("MEMORY['", '').replace("']", ''));
        if (!keys.length) return true;

        // Índices de sistema/posição são válidos mesmo antes de existirem no cache:
        // AI_SIGNAL e MARKET_REGIME são escritos pelo grafo compartilhado (análise/scanner);
        // LAST_ORDER_* é estado de posição (ausente até haver ordem aberta). A saída já se
        // protege com o guard MEMORY['...LAST_ORDER...'] &&.
        const isSystemKey = k => /:(AI_SIGNAL|MARKET_REGIME|LAST_ORDER(_\d+)?)$/.test(k);

        const availableKeys = new Set(
            (props.indexes || [])
                .flatMap(ix => (ix.eval.match(/MEMORY\['([^']+)'\]/g) || []))
                .map(m => m.replace("MEMORY['", '').replace("']", ''))
        );

        // Por-chave: cada uma precisa ser de sistema OU estar declarada. Antes era
        // tudo-ou-nada (.every na whitelist), o que pintava de vermelho condições
        // válidas que misturavam TICKER + LAST_ORDER.
        return keys.every(k => isSystemKey(k) || availableKeys.has(k));
    }

    // Cor do badge: paleta ao-vivo do backend (existe/parado/atendida) quando disponível;
    // senão, fallback estrutural instantâneo (vermelho se índice ausente, verde se ok).
    function badgePalette(condition) {
        const live = condColors[condition.eval];
        if (live && CONDITION_PALETTE[live]) return CONDITION_PALETTE[live];
        return isConditionValid(condition.eval) ? CONDITION_PALETTE.green : CONDITION_PALETTE.red;
    }

    function applyConditions(newEval, newIndexes) {
        const parsed = parseConditions(newEval);
        const updated = [...conditions];

        parsed.forEach(incoming => {
            const conflict = findConflict(updated, incoming.eval);
            if (conflict) {
                const pos = updated.findIndex(c => c.eval === conflict.eval);
                updated.splice(pos, 1, incoming);
            } else if (!updated.some(c => c.eval === incoming.eval)) {
                updated.push(incoming);
            }
        });

        setConditions(updated);
        props.onChange(getEventObject('conditions', updated.map(c => c.eval).join(' && ')));

        const existingIndexes = parseIndexes(updated);
        const merged = [...new Set(
            [existingIndexes, newIndexes].filter(Boolean).join(',').split(',').filter(Boolean)
        )].join(',');
        props.onChange(getEventObject('indexes', merged));
    }

    function onTemplateChange(event) {
        const val = event.target.value;
        event.target.value = '';
        if (!val) return;

        const [source, idStr] = val.split(':');
        if (source === 'fixed') {
            const idx = parseInt(idStr);
            const template = getFixedTemplates(props.symbol, props.indexes)[idx];
            if (template && template.conditions)
                applyConditions(template.conditions, template.indexes || '');
        } else if (source === 'user') {
            const id = parseInt(idStr);
            const template = userTemplates.find(t => t.id === id);
            if (template)
                applyConditions(template.conditions, template.indexes || '');
        }
    }

    function onKeySelectChange(event) {
        const item = props.indexes.find(k => k.eval === event.target.value);
        if (item) setSelectedIndex(item);
    }

    function onAddConditionClick(event) {
        const parsedConditions = parseConditions(event.target.value.eval)[0];
        if (conditions.some(c => c.eval === parsedConditions.eval)) return;

        const updated = [...conditions, parsedConditions];
        setConditions(updated);
        props.onChange(getEventObject('conditions', updated.map(c => c.eval).join(' && ')));
        props.onChange(getEventObject('indexes', parseIndexes(updated)));
        setAddMode(false);
    }

    function btnRemoveCondition(event) {
        const id = event.target.id;
        const updated = conditions.filter(c => c.eval !== id);
        setConditions(updated);
        if (props.onChange) props.onChange(getEventObject('conditions', updated.map(c => c.eval).join(' && ')));
        if (props.onChange) props.onChange(getEventObject('indexes', parseIndexes(updated)));
    }

    function onConfluenceAdd(confluenceConditions, confluenceIndexes) {
        const parsed = parseConditions(confluenceConditions);
        const newConditions = [...conditions];
        parsed.forEach(c => {
            if (!newConditions.some(existing => existing.eval === c.eval))
                newConditions.push(c);
        });
        setConditions(newConditions);
        props.onChange(getEventObject('conditions', newConditions.map(c => c.eval).join(' && ')));

        const existingIndexes = parseIndexes(newConditions);
        const merged = [...new Set([existingIndexes, confluenceIndexes].filter(Boolean).join(',').split(',').filter(Boolean))].join(',');
        props.onChange(getEventObject('indexes', merged));
    }

    async function onSaveTemplate() {
        if (!templateName.trim()) {
            setTemplateError('Informe um nome.');
            return;
        }
        try {
            const conditionsStr = conditions.map(c => c.eval).join(' && ');
            const indexesStr = parseIndexes(conditions);
            const saved = await saveConditionTemplate({ name: templateName.trim(), conditions: conditionsStr, indexes: indexesStr });
            setUserTemplates(prev => [...prev, saved]);
            setTemplateName('');
            setSaveMode(false);
            setTemplateError('');
        } catch (err) {
            setTemplateError(processError(err));
        }
    }

    async function onDeleteUserTemplate(id) {
        try {
            await deleteConditionTemplate(id);
            setUserTemplates(prev => prev.filter(t => t.id !== id));
        } catch (_) { }
    }

    useEffect(() => {
        getConditionTemplates()
            .then(setUserTemplates)
            .catch(() => { });
    }, []);

    useEffect(() => {
        setConditions(parseConditions(props.conditions));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.conditions]);

    useEffect(() => {
        setIndexes(props.indexes);
    }, [props.indexes]);

    // Cores ao-vivo dos badges (caminho frio: só quando o modal abre ou as condições
    // mudam — eventos discretos de add/remove/template). Debounce coalesce rajadas.
    useEffect(() => {
        const evals = conditions.map(c => c.eval).filter(Boolean);
        if (!evals.length) { setCondColors({}); return; }
        const t = setTimeout(() => {
            diagnoseConditions(props.symbol, evals)
                .then(list => {
                    const map = {};
                    (list || []).forEach(item => { map[item.expr] = item.color; });
                    setCondColors(map);
                })
                .catch(() => { });
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conditions, props.symbol]);

    useEffect(() => {
        setSelectedIndex({ example: 0 });
        setAddMode(false);
    }, [props.symbol]);

    const fixedTemplates = getFixedTemplates(props.symbol, props.indexes || []);
    const fixedGroups = [...new Set(fixedTemplates.map(t => t.group))]
        .filter(g => g !== TEMPLATE_GROUPS.INDICADORES);

    return (
        <React.Fragment>
            <RegimeConfluenceHelper indexes={props.indexes} symbol={props.symbol} onAdd={onConfluenceAdd} />

            <div className='row mb-2'>
                <div className='col-12'>
                    <select className='form-select form-select-sm' onChange={onTemplateChange} defaultValue=''>
                        <option value=''>— Aplicar template —</option>
                        {fixedGroups.map(group => (
                            <optgroup key={group} label={group}>
                                {fixedTemplates
                                    .filter(t => t.group === group && t.conditions)
                                    .map((t, idx) => (
                                        <option key={idx} value={`fixed:${fixedTemplates.indexOf(t)}`}>
                                            {t.name}
                                        </option>
                                    ))}
                            </optgroup>
                        ))}
                        {fixedTemplates.some(t => t.group === TEMPLATE_GROUPS.INDICADORES && !t.conditions) && (
                            <optgroup label='Indicadores (adicione o monitor primeiro)'>
                                {fixedTemplates
                                    .filter(t => t.group === TEMPLATE_GROUPS.INDICADORES && !t.conditions)
                                    .map((t, idx) => (
                                        <option key={idx} disabled>{t.name}</option>
                                    ))}
                            </optgroup>
                        )}
                        {userTemplates.length > 0 && (
                            <optgroup label='Meus templates'>
                                {userTemplates.map(t => (
                                    <option key={t.id} value={`user:${t.id}`}>{t.name}</option>
                                ))}
                            </optgroup>
                        )}
                    </select>
                </div>
            </div>

            {conditions.length > 0 && (
                <div className='conditionsBadges mb-2'>
                    {conditions.map((condition, ix) => (
                        <SmartBadge key={ix} id={condition.eval} text={condition.text} onClick={btnRemoveCondition}
                            palette={badgePalette(condition)} />
                    ))}
                </div>
            )}

            {userTemplates.length > 0 && (
                <div className='mb-2'>
                    {userTemplates.map(t => (
                        <span key={t.id} className='badge bg-secondary me-1' style={{ cursor: 'default' }}>
                            {t.name}
                            <button type='button' className='btn-close btn-close-white ms-1'
                                style={{ fontSize: '0.5rem' }}
                                onClick={() => onDeleteUserTemplate(t.id)}
                                aria-label='Remover template' />
                        </span>
                    ))}
                </div>
            )}

            {addMode ? (
                <div className='row'>
                    <div className='col-12 mb-2'>
                        <IndexSelect indexes={indexes} onChange={onKeySelectChange} />
                        <VariableInput indexes={indexes} onAddClick={onAddConditionClick} selectedIndex={selectedIndex} />
                        <button type='button' className='btn btn-link btn-sm text-secondary p-0 mt-1'
                            onClick={() => setAddMode(false)}>
                            Cancelar
                        </button>
                    </div>
                </div>
            ) : (
                <div className='d-flex gap-2 flex-wrap mt-1'>
                    <button type='button' className='btn btn-outline-warning btn-sm'
                        onClick={() => setAddMode(true)}>
                        + Adicionar condição
                    </button>
                    {conditions.length > 0 && !saveMode && (
                        <button type='button' className='btn btn-outline-secondary btn-sm'
                            onClick={() => setSaveMode(true)}>
                            Salvar como template
                        </button>
                    )}
                </div>
            )}

            {saveMode && (
                <div className='row mt-2'>
                    <div className='col-12'>
                        <div className='input-group input-group-sm'>
                            <input
                                type='text'
                                className='form-control'
                                placeholder='Nome do template'
                                value={templateName}
                                onChange={e => setTemplateName(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && onSaveTemplate()}
                            />
                            <button type='button' className='btn btn-primary' onClick={onSaveTemplate}>Salvar</button>
                            <button type='button' className='btn btn-outline-secondary'
                                onClick={() => { setSaveMode(false); setTemplateName(''); setTemplateError(''); }}>
                                Cancelar
                            </button>
                        </div>
                        {templateError && <small className='text-danger'>{templateError}</small>}
                    </div>
                </div>
            )}
        </React.Fragment>
    );
}

export default ConditionsArea;
